import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import cytoscape from '../../../../dependencies/cytoscape.js/build/cytoscape.esm.min.js';
import {
  compileOrganizationGraph,
  type OrganizationGraphCompileResult,
  type OrgNodeData,
} from './utils/organizationGraph';
import {
  applyEdgeStyleSmooth,
  applyNodeStyleSmooth,
  type CytoscapeStyle,
} from './utils/cytoscapeVisual';
import { panElementIntoView } from './utils/cytoscapeViewport';
import { useConfig } from '../../../../hooks/useConfig';
import type { OrganizationDetail } from '../../../../api';
import type { OrganizationRuntimeState } from './types';

const DEPARTMENT_POLYGON_POINTS =
  '-1 -0.55, 0 -0.9, 1 -0.55, 1 0.55, 0 0.9, -1 0.55';
const COMPANY_POLYGON_POINTS = '-1 -0.72, 0 -1, 1 -0.72, 1 0.72, 0 1, -1 0.72';
/** 人员真实头像使用 1:1 比例（圆形裁切），并通过 cover 强制适配。 */
const PERSON_AVATAR_SIZE = 42;
const PERSON_AVATAR_LEFT = 20;
const ORG_OVERLAY_SYNC_DEBUG = false;
/** demo 头像映射：姓名 -> public/static-files/testavatar 下的文件名。 */
const PERSON_AVATAR_DEMO_FILE_MAP: Record<string, string> = {
  邓晓彬: 'xb.png',
  郭俊玺: 'gz.png',
  黄忠鸿: 'hzh.png',
  汤大伟: 'dw.png',
  郭芸溪: 'yx.png',
  杨柳: 'yl.png',
  吴磊: 'xb.png',
  王浩: 'yx.png',
  陈杰: 'dw.png',
  杨欣: 'yl.png',
};

interface OrganizationDiagramProps {
  /** 通过 GET /organizations/{id} 获取的组织详情 */
  detail: OrganizationDetail | null;
  /** 组织节点 id → 运行时状态（应募/符合/不符合） */
  orgStates: Map<string, OrganizationRuntimeState>;
  suppressMotion?: boolean;
  /** 编译完成后回调，向上提供 name→id 映射等 */
  onCompiled?: (graph: OrganizationGraphCompileResult) => void;
  /**
   * 组织图展示模式。
   *
   * - `runtime`：运行过程页使用，保留人员卡片状态徽标
   * - `management`：组织管理页使用，只保留点亮/点暗，不展示勾选/感叹号
   */
  mode?: 'runtime' | 'management';
}

/**
 * 组织架构图组件（第二步 + 过渡期使用）。
 *
 * 本版改用“编译阶段预设坐标 + 样式阶段主题渲染”的方式，
 * 使布局更贴近设计图：公司/部门上下分层，人员卡片全部位于部门右下方。
 */
const OrganizationDiagram: React.FC<OrganizationDiagramProps> = ({
  detail,
  orgStates,
  suppressMotion = false,
  onCompiled,
  mode = 'runtime',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);
  const avatarOverlayRefs = useRef<Map<string, HTMLImageElement>>(new Map());
  /** 头像加载失败后记录节点 id，直接回退到默认图标。 */
  const [brokenAvatarIds, setBrokenAvatarIds] = useState<Set<string>>(
    new Set()
  );
  /** 记录上一轮已应用到画布上的可视状态，便于做增量更新，避免整图闪烁。 */
  const visualStatesRef = useRef<
    Map<string, OrganizationRuntimeState['status']>
  >(new Map());

  const compiled = useMemo<OrganizationGraphCompileResult | null>(() => {
    if (!detail) return null;
    return compileOrganizationGraph(detail);
  }, [detail]);
  const { globalConfig } = useConfig();
  const cfg = globalConfig as { enablePersonAvatarDemo?: boolean };
  const enablePersonAvatarDemo = cfg.enablePersonAvatarDemo !== false;
  /** 管理页不需要人员卡片左上角的勾选/感叹号。 */
  const showPersonStatusBadge = mode === 'runtime';

  useEffect(() => {
    if (compiled && onCompiled) onCompiled(compiled);
  }, [compiled, onCompiled]);

  // 初始化 cytoscape
  useEffect(() => {
    if (!containerRef.current || !compiled) return;

    const cy = cytoscape({
      container: containerRef.current,
      elements: compiled.elements,
      style: buildStylesheet(showPersonStatusBadge, enablePersonAvatarDemo),
      layout: {
        name: 'preset',
        fit: false,
        padding: 40,
      } as any,
      userZoomingEnabled: true,
      userPanningEnabled: true,
      autoungrabify: true,
      boxSelectionEnabled: false,
      minZoom: 0.2,
      maxZoom: 2.5,
      wheelSensitivity: 0.2,
    });
    cyRef.current = cy;
    visualStatesRef.current = new Map();
    setBrokenAvatarIds(new Set());

    // 预设坐标就位后统一 fit 一次，让组织图初始观感更接近设计图。
    cy.fit(cy.elements(), 48);

    return () => {
      cy.destroy();
      cyRef.current = null;
      visualStatesRef.current = new Map();
    };
  }, [compiled, showPersonStatusBadge, enablePersonAvatarDemo]);

  /** 人员真实头像 overlay：先生成静态描述，位置由 rAF + DOM 直写同步。 */
  const personAvatarOverlays = useMemo(() => {
    const cy = cyRef.current;
    if (!cy || !compiled) return [];

    return compiled.personIds
      .map(id => {
        if (brokenAvatarIds.has(id)) return null;
        const node = cy.getElementById(id);
        if (!node || node.empty()) return null;
        const apiAvatarUrl = String(node.data('avatarUrl') || '').trim();
        const personName = String(node.data('originalName') || '').trim();
        const demoAvatarUrls = resolvePersonDemoAvatarUrls(
          personName,
          enablePersonAvatarDemo
        );
        const avatarUrl = apiAvatarUrl || demoAvatarUrls[0] || '';
        const fallbackAvatarUrl = apiAvatarUrl
          ? demoAvatarUrls[0] || ''
          : demoAvatarUrls[1] || '';
        if (!avatarUrl) return null;
        return {
          id,
          avatarUrl,
          fallbackAvatarUrl,
        };
      })
      .filter(Boolean) as Array<{
      id: string;
      avatarUrl: string;
      fallbackAvatarUrl: string;
    }>;
  }, [compiled, brokenAvatarIds, enablePersonAvatarDemo]);

  /** 使用 cytoscape 视口事件 + rAF 直接写 DOM，减少 React 重渲染导致的拖动滞后。 */
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;

    let rafId: number | null = null;
    const syncAvatarOverlayPositions = () => {
      personAvatarOverlays.forEach(overlay => {
        const image = avatarOverlayRefs.current.get(overlay.id);
        if (!image) return;
        const node = cy.getElementById(overlay.id);
        if (!node || node.empty()) {
          image.style.display = 'none';
          return;
        }

        const bb = node.renderedBoundingBox({ includeLabels: false } as any);
        const renderedHeight = bb.y2 - bb.y1;
        const baseHeight = Number(node.data('nodeHeight')) || 84;
        const scale = baseHeight > 0 ? renderedHeight / baseHeight : 1;
        const size = PERSON_AVATAR_SIZE * scale;
        const left = bb.x1 + PERSON_AVATAR_LEFT * scale;
        const top = bb.y1 + (renderedHeight - size) / 2;

        image.style.display = 'block';
        image.style.transform = `translate3d(${left}px, ${top}px, 0)`;
        image.style.width = `${size}px`;
        image.style.height = `${size}px`;
        image.style.borderWidth = `${Math.max(1, 2 * scale)}px`;
      });

      if (ORG_OVERLAY_SYNC_DEBUG) {
        console.debug('[OrganizationDiagram] avatar overlay sync', {
          count: personAvatarOverlays.length,
          zoom: cy.zoom(),
          pan: cy.pan(),
        });
      }
    };

    const scheduleSync = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        syncAvatarOverlayPositions();
      });
    };

    scheduleSync();
    (cy as any).on('pan zoom resize layoutstop render', scheduleSync);

    return () => {
      (cy as any).off('pan zoom resize layoutstop render', scheduleSync);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [personAvatarOverlays]);

  const handleAvatarLoadError = (id: string) => {
    setBrokenAvatarIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // 状态同步到节点 class
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !compiled) return;

    const visualStates = new Map<string, OrganizationRuntimeState['status']>();
    orgStates.forEach((state, id) => {
      visualStates.set(id, state.status);

      // 子节点一旦被招募流程触达，其父链路也应同步点亮为 applied。
      let parentNodes = (cy.getElementById(id) as any).incomers('node');
      while (parentNodes && !parentNodes.empty()) {
        const parentNode = parentNodes[0];
        if (!visualStates.has(parentNode.id())) {
          visualStates.set(parentNode.id(), 'applied');
        }
        parentNodes = parentNode.incomers('node');
      }
    });

    const prevStates = visualStatesRef.current;
    let pendingFocusElement: any = null;

    // 使用 batch 合并渲染，并且只更新差异节点；这点与过程图一致，可避免整图先灰后亮。
    (cy as any).batch(() => {
      prevStates.forEach((_prevStatus, id) => {
        if (visualStates.has(id)) return;
        const node = cy.getElementById(id);
        if (!node || node.empty()) return;
        setNodeStatusClass(node as any, 'pending');
        applyNodeVisualState(
          node as any,
          'pending',
          false,
          showPersonStatusBadge,
          enablePersonAvatarDemo
        );
        applyIncomingEdgeState(node as any, false, !suppressMotion);
      });

      visualStates.forEach((status, id) => {
        if (prevStates.get(id) === status) return;
        const node = cy.getElementById(id);
        if (!node || node.empty()) return;
        setNodeStatusClass(node as any, status);
        applyNodeVisualState(
          node as any,
          status,
          !suppressMotion,
          showPersonStatusBadge,
          enablePersonAvatarDemo
        );
        applyIncomingEdgeState(
          node as any,
          status !== 'pending',
          !suppressMotion
        );
        if (!suppressMotion) {
          pendingFocusElement = node;
        }
      });
    });

    visualStatesRef.current = visualStates;
    if (!suppressMotion) {
      panElementIntoView(cy, pendingFocusElement);
    }
  }, [
    orgStates,
    compiled,
    showPersonStatusBadge,
    enablePersonAvatarDemo,
    suppressMotion,
  ]);

  return (
    <div className="relative h-full w-full bg-white">
      <div ref={containerRef} className="h-full w-full" />
      {personAvatarOverlays.map(overlay => (
        <img
          key={overlay.id}
          ref={image => {
            if (image) {
              avatarOverlayRefs.current.set(overlay.id, image);
            } else {
              avatarOverlayRefs.current.delete(overlay.id);
            }
          }}
          src={overlay.avatarUrl}
          alt=""
          className="pointer-events-none absolute rounded-full border-solid border-white object-cover shadow-[0_1px_4px_rgba(15,23,42,0.16)]"
          style={{
            left: 0,
            top: 0,
            width: PERSON_AVATAR_SIZE,
            height: PERSON_AVATAR_SIZE,
            borderWidth: 2,
            aspectRatio: '1 / 1',
            zIndex: 3,
            transform: 'translate3d(0, 0, 0)',
          }}
          data-fallback-src={overlay.fallbackAvatarUrl}
          onError={event => {
            const target = event.currentTarget;
            const fallbackSrc = (target.dataset.fallbackSrc || '').trim();
            if (fallbackSrc && target.getAttribute('src') !== fallbackSrc) {
              target.setAttribute('src', fallbackSrc);
              target.dataset.fallbackSrc = '';
              return;
            }
            handleAvatarLoadError(overlay.id);
          }}
        />
      ))}
      {/* 图例 */}
      {/* <div className="pointer-events-none absolute right-4 top-3 flex items-center gap-3 rounded-md bg-white/90 px-3 py-1.5 text-[11px] text-slate-500 shadow-sm">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
          符合招募
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-rose-500" />
          不符合
        </span>
      </div> */}
    </div>
  );
};

export default memo(OrganizationDiagram);

/** 拼接 public 目录静态资源链接，兼容非根路径部署（Vite BASE_PATH）。 */
function buildPublicAssetUrl(relativePath: string): string {
  const basePath = import.meta.env.VITE_BASE_PATH || '/';
  const normalizedBasePath =
    basePath === '/' ? '' : basePath.replace(/\/+$/, '');
  return `${normalizedBasePath}/${relativePath.replace(/^\/+/, '')}`;
}

/** 根据姓名生成 demo 头像链接；未命中或开关关闭时返回空字符串。 */
function resolvePersonDemoAvatarUrls(name: string, enabled: boolean): string[] {
  if (!enabled) return [];
  const fileName = PERSON_AVATAR_DEMO_FILE_MAP[name.trim()];
  if (!fileName) return [];

  const relativePath = `static-files/testavatar/${fileName}`;
  const withBasePath = buildPublicAssetUrl(relativePath);
  const rootPath = `/${relativePath}`;
  if (withBasePath === rootPath) return [withBasePath];
  return [withBasePath, rootPath];
}

/** 命中接口头像或 demo 头像时，隐藏卡片内默认头像图标（但保留头像占位）。 */
function shouldHidePersonDefaultAvatar(
  nodeData: Partial<OrgNodeData>,
  enablePersonAvatarDemo: boolean
): boolean {
  const apiAvatarUrl = String(nodeData.avatarUrl || '').trim();
  if (apiAvatarUrl) return true;

  const personName = String(nodeData.originalName || '').trim();
  return (
    resolvePersonDemoAvatarUrls(personName, enablePersonAvatarDemo).length > 0
  );
}

// =============================================================
// 样式表
// =============================================================
function buildStylesheet(
  showPersonStatusBadge = true,
  enablePersonAvatarDemo = true
): any[] {
  return [
    {
      selector: 'node',
      style: {
        label: 'data(label)',
        'text-wrap': 'wrap',
        'text-max-width': (ele: any) => ele.data('nodeWidth') || 180,
        'text-valign': 'center',
        'text-halign': 'center',
        'font-size': 12,
        'font-family':
          'system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif',
        color: '#94a3b8',
        'background-color': '#f3f4f6',
        'border-width': 1.2,
        'border-color': '#d1d5db',
        width: (ele: any) => ele.data('nodeWidth') || 150,
        height: (ele: any) => ele.data('nodeHeight') || 44,
        shape: 'round-rectangle',
        // 组织图会在招募阶段密集收到流式事件；关闭 cytoscape transition，避免连续样式更新造成闪烁。
        'transition-property': 'none',
        'transition-duration': 0,
      } as any,
    },
    {
      selector: 'node.company',
      style: {
        shape: 'polygon',
        'shape-polygon-points': COMPANY_POLYGON_POINTS,
        label: '',
        'background-image': (ele: any) =>
          makeCompanyCardDataUri(ele.data() as OrgNodeData, 'pending'),
        'background-image-opacity': 1,
        'background-fit': 'none',
        'background-repeat': 'no-repeat',
        'background-width': '100%',
        'background-height': '100%',
        'background-position-x': '50%',
        'background-position-y': '50%',
        'background-width-relative-to': 'inner',
        'background-height-relative-to': 'inner',
        'background-clip': 'node',
        'background-image-containment': 'inside',
        'background-color': '#111111',
        'border-color': '#111111',
        color: '#ffffff',
      } as any,
    },
    {
      selector: 'node.department',
      style: {
        shape: 'polygon',
        'shape-polygon-points': DEPARTMENT_POLYGON_POINTS,
        'font-size': 14,
        'font-weight': 'bold',
        color: '#ffffff',
      } as any,
    },
    {
      selector: 'node.person',
      style: {
        shape: 'round-rectangle',
        // 人员卡片改为整张 SVG 内容图，这样可以稳定实现左对齐、多字号和不同字重。
        label: '',
        'background-image': (ele: any) =>
          makePersonCardDataUri(
            ele.data() as OrgNodeData,
            'pending',
            showPersonStatusBadge,
            shouldHidePersonDefaultAvatar(
              ele.data() as Partial<OrgNodeData>,
              enablePersonAvatarDemo
            )
          ),
        'background-image-opacity': 1,
        'background-fit': 'none',
        'background-repeat': 'no-repeat',
        'background-width': '100%',
        'background-height': '100%',
        'background-position-x': '50%',
        'background-position-y': '50%',
        'background-width-relative-to': 'inner',
        'background-height-relative-to': 'inner',
        'background-clip': 'node',
        'background-image-containment': 'inside',
      } as any,
    },
    {
      selector: 'edge',
      style: {
        width: 1.5,
        'line-color': '#d5d9e2',
        'target-arrow-color': '#d5d9e2',
        'target-arrow-shape': 'none',
        'curve-style': 'taxi',
        'taxi-direction': 'downward',
        'taxi-turn': 38,
        'taxi-turn-min-distance': '18px',
        'line-cap': 'round',
        // 边线点亮统一走显式动画工具，避免全局 transition 被每条日志重启。
        'transition-property': 'none',
        'transition-duration': 0,
      } as any,
    },
    {
      selector: 'edge.person-edge',
      style: {
        // 这里直接复用 OPMCV 结构边思路：同一部门的多条人员边共享左侧竖向主干，再向右分叉。
        'curve-style': 'taxi',
        'taxi-direction': 'downward',
        'taxi-turn': (ele: any) => getPersonEdgeTaxiTurn(ele),
        'taxi-turn-min-distance': '18px',
        'source-endpoint': '0px 50%',
        'target-endpoint': '-50% 0px',
        'source-distance-from-node': 0,
        'target-distance-from-node': 0,
      } as any,
    },
    {
      selector: 'edge.root-edge',
      style: {
        width: 1.8,
        'curve-style': 'taxi',
        'taxi-direction': 'downward',
        // 根节点到二级部门单独保留一套更明显的折线，避免长水平线贴着根节点底部扫过去。
        'taxi-turn': (ele: any) => getRootEdgeTaxiTurn(ele),
        'taxi-turn-min-distance': '28px',
        'source-endpoint': '0px 50%',
        'target-endpoint': '0px -50%',
        'source-distance-from-node': 0,
        'target-distance-from-node': 0,
      } as any,
    },
    {
      selector: 'edge.dept-edge',
      style: {
        'curve-style': 'taxi',
        'taxi-direction': 'downward',
        // 子部门与直属人员在视觉上属于同一层级，因此复用人员连线的折线规则。
        'taxi-turn': (ele: any) => getPersonEdgeTaxiTurn(ele),
        'taxi-turn-min-distance': '18px',
        'source-endpoint': '0px 50%',
        // 子部门从左侧接入，和同层人员卡片保持一致的缩进视觉。
        'target-endpoint': '-50% 0px',
        'source-distance-from-node': 0,
        'target-distance-from-node': 0,
      } as any,
    },
  ];
}

/**
 * 组织图边线默认保持灰色；节点/部门真正进入招募链路后再按主题色点亮。
 */
function applyIncomingEdgeState(node: any, active: boolean, animated = true) {
  const incomingEdges = node.incomers('edge');
  if (!incomingEdges || incomingEdges.empty()) return;

  incomingEdges.forEach((edge: any) => {
    if (!active) {
      edge.removeClass('activated');
      edge.removeStyle('line-color target-arrow-color width opacity');
      return;
    }

    const themeColor = (edge.data('themeColor') as string) || '#94a3b8';
    const nextWidth = edge.hasClass('root-edge') ? 1.8 : 1.5;
    edge.addClass('activated');
    const nextStyle: CytoscapeStyle = {
      'line-color': themeColor,
      'target-arrow-color': themeColor,
      width: nextWidth,
      opacity: 1,
    };
    if (animated) {
      applyEdgeStyleSmooth(edge, nextStyle);
    } else {
      edge.stop(true, false);
      edge.style(nextStyle);
    }
  });
}

/** 统一维护组织图节点的状态 class，便于调试和和现有样式规则保持一致。 */
function setNodeStatusClass(
  node: any,
  status: OrganizationRuntimeState['status']
) {
  const allStatus = [
    'status-pending',
    'status-applied',
    'status-approved',
    'status-rejected',
  ];
  for (const cls of allStatus) node.removeClass(cls);
  node.addClass(`status-${status}`);
}

/**
 * 组织图节点直接写入 bypass 样式，避免 class 动态切换后节点颜色没有及时刷新。
 */
function applyNodeVisualState(
  node: any,
  status: OrganizationRuntimeState['status'],
  animated: boolean,
  showPersonStatusBadge = true,
  enablePersonAvatarDemo = true
) {
  const kind = node.data('kind') as string;
  const themeColor = (node.data('themeColor') as string) || '#94a3b8';
  const themeSoftBg = (node.data('themeSoftBg') as string) || '#f3f4f6';
  const applyStyle = (style: CytoscapeStyle) => {
    if (animated) {
      applyNodeStyleSmooth(node, style, { fallbackBorderWidth: 1.2 });
    } else {
      node.stop(true, false);
      node.style(style);
    }
  };

  if (kind === 'company') {
    applyCompanyDecor(node, status);
  }

  if (kind === 'person') {
    applyPersonDecor(
      node,
      status,
      showPersonStatusBadge,
      enablePersonAvatarDemo
    );
  }

  if (status === 'pending') {
    if (kind === 'company') {
      applyStyle({
        'background-color': '#e5e7eb',
        'border-color': '#e5e7eb',
        color: '#9ca3af',
        opacity: 1,
      });
      return;
    }

    if (kind === 'department') {
      applyStyle({
        'background-color': '#e5e7eb',
        'border-color': '#e5e7eb',
        color: '#9ca3af',
        opacity: 1,
      });
      return;
    }

    applyStyle({
      'background-color': '#f8fafc',
      'border-color': '#dbe1ea',
      color: '#94a3b8',
      opacity: 1,
    });
    return;
  }

  if (kind === 'company') {
    applyStyle({
      'background-color': '#111111',
      'border-color': '#111111',
      color: '#ffffff',
      opacity: 1,
    });
    return;
  }

  if (kind === 'department') {
    applyStyle({
      'background-color': themeColor,
      'border-color': themeColor,
      color: '#ffffff',
      opacity: 1,
    });
    return;
  }

  if (status === 'applied') {
    applyStyle({
      'background-color': '#ffffff',
      'border-color': themeColor,
      color: themeColor,
      opacity: 1,
    });
    return;
  }

  // 设计图里人员卡片无论通过还是不通过，都保持所属部门的主题色浅底；
  // 差异只体现在左上角状态徽标上。
  applyStyle({
    'background-color': themeSoftBg,
    'border-color': themeColor,
    color: '#1f2937',
    opacity: 1,
  });
}

/** 根据当前状态重绘整张人员卡片内容图。 */
function applyPersonDecor(
  node: any,
  status: OrganizationRuntimeState['status'],
  showPersonStatusBadge = true,
  enablePersonAvatarDemo = true
) {
  const nodeData = node.data() as OrgNodeData;
  node.style({
    'background-image': makePersonCardDataUri(
      nodeData,
      status,
      showPersonStatusBadge,
      shouldHidePersonDefaultAvatar(nodeData, enablePersonAvatarDemo)
    ),
    'background-image-opacity': 1,
    'background-width': '100%',
    'background-height': '100%',
    'background-position-x': '50%',
    'background-position-y': '50%',
    'background-repeat': 'no-repeat',
  });
}

/** 公司根节点使用 SVG 文字层，支持公司名与 owner 使用不同字号。 */
function applyCompanyDecor(
  node: any,
  status: OrganizationRuntimeState['status']
) {
  node.style({
    'background-image': makeCompanyCardDataUri(
      node.data() as Partial<OrgNodeData>,
      status
    ),
    'background-image-opacity': 1,
    'background-width': '100%',
    'background-height': '100%',
    'background-position-x': '50%',
    'background-position-y': '50%',
    'background-repeat': 'no-repeat',
  });
}

/**
 * 组织图人员连线对齐 OPMCV 结构边：
 * - 第一段尽量沿部门底部向下延伸，形成共享主干
 * - 到各自人员卡片中心高度后，再水平接到卡片左侧
 */
function getPersonEdgeTaxiTurn(edge: any) {
  const source = edge.source?.();
  const target = edge.target?.();
  if (!source || !target) return 1000000;

  const sourceY = source.position('y');
  const targetY = target.position('y');
  const verticalDelta = sourceY - targetY;
  return verticalDelta > -105 ? 84 : 1000000;
}

/** 根节点到二级节点的折线需要更明显的下探，再横向分发到各一级部门。 */
function getRootEdgeTaxiTurn(edge: any) {
  const source = edge.source?.();
  const target = edge.target?.();
  if (!source || !target) return 56;

  const sourceY = source.position('y');
  const sourceHeight = source.outerHeight?.() || source.height?.() || 0;
  const targetY = target.position('y');
  const targetHeight = target.outerHeight?.() || target.height?.() || 0;
  const verticalGap = targetY - targetHeight / 2 - (sourceY + sourceHeight / 2);

  return Math.max(42, Math.min(88, verticalGap * 0.62));
}

/**
 * 生成整张人员卡片内容 SVG。
 *
 * 这里不再依赖 Cytoscape 原生 label 排版，
 * 而是把头像、状态徽标、姓名和角色一起绘制成一张背景图，
 * 以精确贴近设计图中的左对齐和双字号排版。
 */
function makePersonCardDataUri(
  nodeData: Partial<OrgNodeData>,
  status: OrganizationRuntimeState['status'],
  showPersonStatusBadge = true,
  hideDefaultAvatar = false
): string {
  const width = Number(nodeData.nodeWidth) || 210;
  const height = Number(nodeData.nodeHeight) || 84;
  const name = fitTextToLine(nodeData.originalName || '', width - 110, 17);
  const subtitleLines = wrapTextLines(
    nodeData.subtitle || '',
    width - 110,
    13,
    2
  );
  const avatarColor = status === 'pending' ? '#98A2B3' : '#475467';
  const nameColor = status === 'pending' ? '#98A2B3' : '#344054';
  const subtitleColor = status === 'pending' ? '#98A2B3' : '#475467';
  // 按总行数做垂直基线：三行（姓名+职位两行）保持现状，两行时整体下移确保视觉居中。
  const textLineCount = 1 + subtitleLines.length;
  const nameY = textLineCount >= 3 ? 28 : textLineCount === 2 ? 38 : 43;
  const subtitleStartY = textLineCount >= 3 ? 47 : textLineCount === 2 ? 57 : 0;
  const subtitleSvg = subtitleLines
    .map(
      (line, index) =>
        `<text x='86' y='${subtitleStartY + index * 18}' fill='${subtitleColor}' font-size='13' font-weight='500'>${escapeSvgText(line)}</text>`
    )
    .join('');
  const badgeSvg = showPersonStatusBadge
    ? status === 'approved'
      ? makeStatusBadgeSvgMarkup('approved')
      : status === 'rejected'
        ? makeStatusBadgeSvgMarkup('rejected')
        : ''
    : '';
  const avatarCenterX = PERSON_AVATAR_LEFT + PERSON_AVATAR_SIZE / 2;
  const avatarSvg = hideDefaultAvatar
    ? ''
    : makePersonAvatarSvgMarkup(
        avatarColor,
        '#ffffff',
        avatarCenterX,
        height / 2,
        18
      );
  const svg = `<?xml version="1.0" encoding="UTF-8"?><!DOCTYPE svg>
  <svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${width} ${height}'>
    ${badgeSvg}
    ${avatarSvg}
    <g font-family='system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif'>
      <text x='86' y='${nameY}' fill='${nameColor}' font-size='17' font-weight='700'>${escapeSvgText(name)}</text>
      ${subtitleSvg}
    </g>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * 公司根节点文本也改为 SVG 绘制：
 * - 第一行公司名更大、更重
 * - 第二行 owner 更小，避免视觉喧宾夺主
 */
function makeCompanyCardDataUri(
  nodeData: Partial<OrgNodeData>,
  status: OrganizationRuntimeState['status']
): string {
  const width = Number(nodeData.nodeWidth) || 320;
  const height = Number(nodeData.nodeHeight) || 88;
  const [rawTitle = '', rawOwner = ''] = String(nodeData.label || '').split(
    '\n'
  );
  const title = fitTextToLine(rawTitle, width - 56, 24);
  const owner = fitTextToLine(rawOwner, width - 72, 16);
  const textColor = status === 'pending' ? '#9ca3af' : '#ffffff';
  const ownerColor = status === 'pending' ? '#9ca3af' : '#d1d5db';
  const hasOwner = !!owner;
  // 两行文本围绕节点垂直中心排布，避免公司名与 owner 整体偏上。
  const titleY = hasOwner ? height / 2 - 12 : height / 2;
  const ownerY = hasOwner ? height / 2 + 14 : 0;
  const ownerStartX = hasOwner
    ? Math.max(24, (width - estimateSvgTextWidth(owner, 16)) / 2)
    : 0;
  const ownerSvg = hasOwner
    ? `<text x='${ownerStartX}' y='${ownerY}' fill='${ownerColor}' font-size='16' font-weight='500' text-anchor='start' dominant-baseline='middle'>${escapeSvgText(owner)}</text>`
    : '';
  const svg = `<?xml version="1.0" encoding="UTF-8"?><!DOCTYPE svg>
  <svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${width} ${height}'>
    <g font-family='system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif'>
      <text x='${width / 2}' y='${titleY}' fill='${textColor}' font-size='24' font-weight='700' text-anchor='middle' dominant-baseline='middle'>${escapeSvgText(title)}</text>
      ${ownerSvg}
    </g>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/** 头像图标仍保持与流程图同一视觉语言，但输出为可嵌入卡片的 SVG 片段。 */
function makePersonAvatarSvgMarkup(
  color: string,
  bg: string,
  centerX: number,
  centerY: number,
  radius: number
): string {
  const headY = centerY - 3;
  const bodyTopY = centerY + 5;
  return `
    <circle cx='${centerX}' cy='${centerY}' r='${radius}' fill='${bg}' stroke='${color}' stroke-width='2'/>
    <circle cx='${centerX}' cy='${headY}' r='5' fill='none' stroke='${color}' stroke-width='2'/>
    <path d='M${centerX - 10} ${bodyTopY + 8}c1.8-4.4 5.6-6.8 10-6.8s8.2 2.4 10 6.8' fill='none' stroke='${color}' stroke-width='2' stroke-linecap='round'/>
  `;
}

/** 状态徽标：通过为绿色勾，不通过为红色感叹号。 */
function makeStatusBadgeSvgMarkup(status: 'approved' | 'rejected'): string {
  const color = status === 'approved' ? '#10B981' : '#EF4444';
  const symbol =
    status === 'approved'
      ? `<path d='M10.2 15.2 12.8 17.9 17.9 11.9' fill='none' stroke='#ffffff' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/>`
      : `<path d='M14 10v4.4' fill='none' stroke='#ffffff' stroke-width='1.8' stroke-linecap='round'/><circle cx='14' cy='17.6' r='1.05' fill='#ffffff'/>`;
  return `
    <circle cx='14' cy='14' r='8' fill='${color}'/>
    ${symbol}
  `;
}

/** SVG 文本需要进行最基本的 XML 转义，避免特殊字符破坏结构。 */
function escapeSvgText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** 人员姓名只保留单行，超出时以省略号截断。 */
function fitTextToLine(
  text: string,
  maxWidth: number,
  fontSize: number
): string {
  const lines = wrapTextLines(text, maxWidth, fontSize, 1);
  return lines[0] || '';
}

/** 按近似字符宽度进行换行，足够用于组织图卡片文本布局。 */
function wrapTextLines(
  text: string,
  maxWidth: number,
  fontSize: number,
  maxLines: number
): string[] {
  if (!text) return [];

  const lines: string[] = [];
  let current = '';
  let currentWidth = 0;

  for (const char of text) {
    const charWidth = estimateSvgCharWidth(char, fontSize);
    if (current && currentWidth + charWidth > maxWidth) {
      lines.push(current);
      current = char;
      currentWidth = charWidth;
      if (lines.length === maxLines) break;
      continue;
    }
    current += char;
    currentWidth += charWidth;
  }

  if (lines.length < maxLines && current) {
    lines.push(current);
  }

  const consumedLength = lines.join('').length;
  if (consumedLength < text.length && lines.length) {
    lines[lines.length - 1] = appendEllipsis(
      lines[lines.length - 1],
      maxWidth,
      fontSize
    );
  }

  return lines;
}

function appendEllipsis(
  text: string,
  maxWidth: number,
  fontSize: number
): string {
  const ellipsis = '…';
  let result = text;
  while (
    result &&
    estimateSvgTextWidth(result + ellipsis, fontSize) > maxWidth
  ) {
    result = result.slice(0, -1);
  }
  return `${result}${ellipsis}`;
}

function estimateSvgTextWidth(text: string, fontSize: number): number {
  let width = 0;
  for (const char of text) {
    width += estimateSvgCharWidth(char, fontSize);
  }
  return width;
}

function estimateSvgCharWidth(char: string, fontSize: number): number {
  return /[\u4e00-\u9fa5]/.test(char) ? fontSize : fontSize * 0.62;
}
